import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useState } from "react";
import { api, type CalendarDto, type EventDto, type OccurrenceDto, type OverviewDto } from "../api/client";
import { CaptureBox } from "../capture/CaptureBox";
import { EventList } from "../components/EventList";
import { addDays, datePart, formatLongDate } from "../format";
import { EventDetails } from "../components/EventDetails";
import { EventForm } from "./CalendarPage";
import { useFamilyNow } from "../components/useFamilyNow";
import { agendaPeriod } from "../agendaGroups";
import { noteRevision } from "../realtime/realtime";
import { useSession } from "../session";

export function TodayPage() {
  const { me } = useSession();
  const now = useFamilyNow(me.family.timeZone);
  const today = datePart(now);
  const [selected, setSelected] = useState<OccurrenceDto | null>(null);
  const [editing, setEditing] = useState<EventDto | null>(null);
  const calendars = useQuery({ queryKey: ["calendars"], queryFn: () => api<CalendarDto[]>("/api/v1/calendars") });
  const overview = useQuery({
    queryKey: ["overview", today],
    queryFn: async () => {
      const o = await api<OverviewDto>("/api/v1/overview");
      noteRevision(o.revision);
      return o;
    },
  });
  const upcoming = (overview.data?.upcoming ?? []).filter(o => datePart(o.start) < addDays(today, 7));
  const groups = [
    { key: "ongoing", label: "Läuft noch" }, { key: "next", label: "Als Nächstes" }, { key: "later", label: "Später" },
  ] as const;
  const past = upcoming.filter(o => agendaPeriod(o, now) === "past");

  return (
    <div className="page">
      <h1>Hallo {me.displayName}</h1>
      <p className="muted">{formatLongDate(today)}</p>
      <div className="tiles compact">
        <Link to="/shopping" className="tile">
          <strong>{overview.data?.openShoppingItems ?? "–"}</strong>
          <span>offene Einkäufe</span>
        </Link>
        <Link to="/inbox" className={`tile${overview.data && overview.data.inboxNeedsAttention > 0 ? " attention" : ""}`}>
          <strong>{overview.data?.inboxNeedsAttention ?? "–"}</strong>
          <span>im Eingang offen</span>
        </Link>
      </div>
      <details className="quick-capture card"><summary>Schnell eintragen</summary><CaptureBox compact /></details>
      {overview.isError && <p className="error">Überblick konnte nicht geladen werden.</p>}
      {groups.map(group => {
        const items = upcoming.filter(o => agendaPeriod(o, now) === group.key);
        return items.length > 0 && <section key={group.key} className={`today-${group.key}`}><h2>{group.label}</h2><EventList occurrences={items} persons={me.persons} onSelect={setSelected} emptyText="" /></section>;
      })}
      {overview.data && upcoming.length === 0 && <p className="muted">Keine Termine in den nächsten sieben Tagen.</p>}
      {past.length > 0 && <details className="past-events"><summary>Bereits vorbei ({past.length})</summary><EventList occurrences={past} persons={me.persons} onSelect={setSelected} emptyText="" /></details>}
      {selected && <EventDetails occurrence={selected} canEdit={!!calendars.data?.find(c => c.id === selected.calendarId)?.canWrite}
        onClose={() => setSelected(null)} onEdit={event => { setSelected(null); setEditing(event); }} />}
      {editing && <EventForm initial={editing} calendars={(calendars.data ?? []).filter(c => c.canWrite)} defaultDate={today} onClose={() => setEditing(null)} />}
    </div>
  );
}
